import { Component, Input, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';

/**
 * Section heading variant:
 * - small: Larger heading (24px) with optional metadata text + divider
 * - tiny: Smaller heading (20px) without metadata
 */
export type SectionHeadingVariant = 'small' | 'tiny';

/**
 * Section Heading Component
 *
 * Section-level heading with icon badge and optional metadata.
 * Matches Figma SectionHeading (node 5377-2719).
 *
 * Figma property → Angular mapping:
 *   - Property 1 ("Small" | "Tiny") → variant: 'small' | 'tiny'
 *   - Icon (24x24 placeholder)      → projected <ng-content select="[sectionIcon]">
 *   - Title text                     → projected <ng-content>
 *   - Metadata text                  → @Input() metadata (Small variant only)
 *
 * Figma specs (container):
 *   - Layout: flex, gap-10px, items-center, full width
 *
 * Figma specs (icon badge):
 *   - Background: --hc-semantic-background-status-info-idle-light (#e7f0fe)
 *   - Border-radius: --hc-border-radius-lg (8px)
 *   - Icon size: 24x24px
 *   - Small: padding 12px/6px, gap 6px, min-width 36px
 *   - Tiny: padding 10px/4px, gap 4px, min-width 28px
 *
 * Figma specs (Small title):
 *   - Font: heading/small (24px, 600, 32px line-height)
 *   - Letter-spacing: -0.6px (no matching token — flagged)
 *   - Color: --hc-semantic-content-general-primary (#1f2937)
 *   - Gap to metadata: 24px
 *   - Metadata divider: 1px vertical, #d9d9d9 (no matching token — flagged)
 *   - Metadata font: label/small (14px, 600, 20px line-height)
 *   - Metadata color: --hc-semantic-content-general-secondary (#4b5563)
 *
 * Figma specs (Tiny title):
 *   - Font: heading/tiny (20px, 600, 28px line-height)
 *   - Letter-spacing: -0.5px (no matching token — flagged)
 *   - Color: --hc-semantic-content-general-secondary (#4b5563)
 *
 * @example
 * ```html
 * <!-- Small with icon and metadata -->
 * <hc-section-heading variant="small" metadata="Booking ID 1900806">
 *   <lucide-icon sectionIcon name="file-text"></lucide-icon>
 *   Guest Information
 * </hc-section-heading>
 *
 * <!-- Tiny with icon -->
 * <hc-section-heading variant="tiny">
 *   <lucide-icon sectionIcon name="settings"></lucide-icon>
 *   Operations
 * </hc-section-heading>
 * ```
 */
@Component({
  selector: 'hc-section-heading',
  standalone: false,
  templateUrl: './section-heading.component.html',
  styleUrl: './section-heading.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SectionHeadingComponent {
  /** Visual variant — maps to Figma "Property 1" */
  @Input() variant: SectionHeadingVariant = 'small';

  /** Optional metadata text displayed after a divider (Small variant only) */
  @Input() metadata = '';
}
